import { timestampDate } from "@/utils/timestamp";
import type { Notification } from "gitpod-next-api/gitpod/v1/notification_pb";
import { NotificationType } from "gitpod-next-api/gitpod/v1/notification_pb";

export type NotificationGroup = {
    key: string;
    type: Notification["type"];
    latest: Notification;
    items: Notification[];
    hasUnread: boolean;
};

const AGENT_EXECUTION_TYPES = new Set([NotificationType.AGENT_COMPLETED, NotificationType.AGENT_WAITING_FOR_INPUT]);

/** Groups by context identity. Agent execution types (completed, waiting for input)
 *  with the same environment share a single group. Other types group by type + context.
 *  No context → unique key, never grouped. */
export function deriveGroupKey(n: Notification): string {
    const ctx = n.context?.context;
    switch (ctx?.case) {
        case "agentExecution": {
            const typeKey = AGENT_EXECUTION_TYPES.has(n.type) ? "agent" : String(n.type);
            return `${typeKey}:env:${ctx.value.environmentId}`;
        }
        case "environment":
            return `${n.type}:env:${ctx.value.environmentId}`;
        case "project":
            return `${n.type}:proj:${ctx.value.projectId}`;
        case "workflowExecution":
            return `${n.type}:wfx:${ctx.value.workflowExecutionId}`;
        case "member": {
            const target = ctx.value.target;
            const targetId =
                target.case === "group" ? target.value.groupId : target.case === "team" ? target.value.teamId : n.id;
            return `${n.type}:member:${targetId}`;
        }
        default:
            return `${n.type}:${n.id}`;
    }
}

export function groupNotifications(notifications: Notification[]): NotificationGroup[] {
    const map = new Map<string, NotificationGroup>();
    const order: string[] = [];

    for (const n of notifications) {
        const key = deriveGroupKey(n);
        const existing = map.get(key);
        if (existing) {
            existing.items.push(n);
            if (!n.readAt) existing.hasUnread = true;
        } else {
            order.push(key);
            map.set(key, {
                key,
                type: n.type,
                latest: n,
                items: [n],
                hasUnread: !n.readAt,
            });
        }
    }

    return order.map((k) => map.get(k)!);
}

// ---------------------------------------------------------------------------
// Time-period sectioning
// ---------------------------------------------------------------------------

export type TimePeriodSection = {
    /** Stable key for React rendering (e.g. "today", "2026-04-09"). */
    key: string;
    /** Human-readable label (e.g. "Today", "Yesterday", "April 9"). */
    label: string;
    /** Unread notification count across all groups in this section. */
    unreadCount: number;
    groups: NotificationGroup[];
};

/** Returns the start-of-day (midnight local time) for a given date. */
function startOfDay(d: Date): Date {
    const r = new Date(d);
    r.setHours(0, 0, 0, 0);
    return r;
}

/** Returns the Monday of the week containing `d` (ISO week: Mon=0). */
function startOfWeek(d: Date): Date {
    const r = startOfDay(d);
    const day = r.getDay();
    // getDay(): 0=Sun, 1=Mon … 6=Sat → shift so Mon=0
    const diff = (day + 6) % 7;
    r.setDate(r.getDate() - diff);
    return r;
}

const dateLabelFormatter = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" });

/**
 * Assigns a section key to a notification group based on the latest item's
 * timestamp relative to `now`.
 *
 * Buckets: Today, Yesterday, This week, Last week, then specific dates
 * (e.g. "April 9") for anything older.
 */
export function deriveSectionKey(group: NotificationGroup, now: Date): { key: string; label: string } {
    const ts = timestampDate(group.latest.createdAt) ?? now;

    const todayStart = startOfDay(now);
    if (ts >= todayStart) {
        return { key: "today", label: "Today" };
    }

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    if (ts >= yesterdayStart) {
        return { key: "yesterday", label: "Yesterday" };
    }

    const thisWeekStart = startOfWeek(now);
    if (ts >= thisWeekStart) {
        return { key: "this-week", label: "This week" };
    }

    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    if (ts >= lastWeekStart) {
        return { key: "last-week", label: "Last week" };
    }

    // Older: group by specific date
    const dateKey = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, "0")}-${String(ts.getDate()).padStart(2, "0")}`;
    const label = dateLabelFormatter.format(ts);
    return { key: dateKey, label };
}

/**
 * Partitions already-grouped notifications into time-period sections.
 * Preserves the input order within each section.
 */
export function groupByTimePeriod(groups: NotificationGroup[], now: Date = new Date()): TimePeriodSection[] {
    const sectionMap = new Map<string, TimePeriodSection>();
    const sectionOrder: string[] = [];

    for (const group of groups) {
        const { key, label } = deriveSectionKey(group, now);
        const unread = group.items.filter((n) => !n.readAt).length;
        const existing = sectionMap.get(key);
        if (existing) {
            existing.groups.push(group);
            existing.unreadCount += unread;
        } else {
            sectionOrder.push(key);
            sectionMap.set(key, {
                key,
                label,
                unreadCount: unread,
                groups: [group],
            });
        }
    }

    return sectionOrder.map((k) => sectionMap.get(k)!);
}
